package app.nestbridge.feature.invitations.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.PhotoCamera
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.karawek.core.ui.components.CoreSecondaryButton
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import app.nestbridge.feature.invitations.screen.InvitationQrScanEvent
import app.nestbridge.feature.invitations.screen.InvitationQrScanScreen
import app.nestbridge.feature.invitations.screen.InvitationQrScanState
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.codegen.annotations.CircuitInject
import nestbridge.composeapp.generated.resources.Res
import nestbridge.composeapp.generated.resources.ic_nestbridge_logo
import org.jetbrains.compose.resources.painterResource
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import com.karawek.core.ui.components.CoreScaffold
import app.nestbridge.feature.invitations.screen.InvitationDetailEvent
import app.nestbridge.feature.invitations.screen.InvitationDetailState
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

@CircuitInject(
    screen = InvitationQrScanScreen::class,
    scope = AppScope::class,
)
@Composable
fun InvitationQrScanUi(
    state: InvitationQrScanState,
    modifier: Modifier = Modifier,
) {
    CoreScaffold(
        title = "Scan Invitation QR",
        subtitle = "Point your camera at the QR code shared by the person inviting you.",
        footer = { Footer(state) },
        onBack = { state.eventSink(InvitationQrScanEvent.Cancel) },
        modifier = modifier,
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(CoreSpacing.lg),
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = CoreSpacing.lg, vertical = CoreSpacing.lg),
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(1f)
                    .clip(RoundedCornerShape(20.dp))
                    .background(CoreTheme.colors.surfaceVariant)
                    .border(
                        width = 1.dp,
                        color = CoreTheme.colors.divider,
                        shape = RoundedCornerShape(20.dp),
                    )
                    .padding(CoreSpacing.lg)
                    .clickable { state.eventSink(InvitationQrScanEvent.SimulateScan) },
                contentAlignment = Alignment.Center,
            ) {
                CornerAccent(
                    modifier = Modifier.align(Alignment.TopStart),
                    color = CoreTheme.colors.textSecondary,
                )
                CornerAccent(
                    modifier = Modifier.align(Alignment.TopEnd),
                    color = CoreTheme.colors.textSecondary,
                    rotation = 90f,
                )
                CornerAccent(
                    modifier = Modifier.align(Alignment.BottomEnd),
                    color = CoreTheme.colors.textSecondary,
                    rotation = 180f,
                )
                CornerAccent(
                    modifier = Modifier.align(Alignment.BottomStart),
                    color = CoreTheme.colors.textSecondary,
                    rotation = 270f,
                )

                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
                ) {
                    Icon(
                        imageVector = Icons.Outlined.PhotoCamera,
                        contentDescription = null,
                        tint = CoreTheme.colors.textSecondary,
                        modifier = Modifier.size(32.dp),
                    )
                    Text(
                        text = "Camera preview would appear here",
                        style = CoreTypography.bodySecondary,
                        color = CoreTheme.colors.textSecondary,
                        textAlign = TextAlign.Center,
                    )
                    Text(
                        text = "(Tap to simulate scan)",
                        style = CoreTypography.caption,
                        color = CoreTheme.colors.textSecondary,
                    )
                }
            }

            Text(
                text = "Scanning only lets you review the invitation - it does not accept it.",
                style = CoreTypography.caption,
                color = CoreTheme.colors.textSecondary,
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}

@Composable
private fun Footer(state: InvitationQrScanState) =
    CoreSecondaryButton(
        text = "Cancel",
        onClick = { state.eventSink(InvitationQrScanEvent.Cancel) },
        modifier = Modifier.fillMaxWidth(),
    )

@Composable
private fun CornerAccent(
    modifier: Modifier,
    color: Color,
    rotation: Float = 0f,
) {
    Box(
        modifier = modifier
            .size(28.dp)
            .padding(CoreSpacing.xs)
            .graphicsLayer(rotationZ = rotation)
            .drawBehind {
                val stroke = Stroke(width = 2.dp.toPx())
                drawLine(
                    color = color,
                    start = Offset.Zero,
                    end = Offset(x = size.width, y = 0f),
                    strokeWidth = stroke.width,
                )
                drawLine(
                    color = color,
                    start = Offset.Zero,
                    end = Offset(x = 0f, y = size.height),
                    strokeWidth = stroke.width,
                )
            },
    )
}

@Preview
@Composable
private fun InvitationQrScanPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        InvitationQrScanUi(
            state = InvitationQrScanState(
                eventSink = {},
            ),
        )
    }
}
